import React from 'react';
import { Box, Container, HStack, Link, Text, Icon, VStack, Image, Spinner } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaSpotify, FaInstagram, FaYoutube, FaFacebook, FaRss } from 'react-icons/fa';
import { SiApplepodcasts } from 'react-icons/si';

const SocialLink = ({ icon, href, label }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="38px"
    h="38px"
    borderRadius="full"
    background="rgba(255,255,255,0.05)"
    border="1px solid rgba(255,255,255,0.08)"
    color="whiteAlpha.800"
    transition="all 0.25s"
    _hover={{
      color: 'brand.300',
      background: 'rgba(255,152,0,0.1)',
      borderColor: 'rgba(255,152,0,0.3)',
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 20px rgba(255,152,0,0.2)',
    }}
  >
    <Icon as={icon} boxSize={4} />
  </Link>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box as="footer" position="relative" overflow="hidden">
      {/* Top glow divider */}
      <Box className="glow-divider" />

      {/* Ambient orb */}
      <Box
        position="absolute"
        top="0"
        left="50%"
        transform="translateX(-50%)"
        w="600px"
        h="200px"
        borderRadius="full"
        bg="rgba(255,152,0,0.04)"
        filter="blur(60px)"
        pointerEvents="none"
      />

      <Box
        bg="rgba(21,13,4,0.95)"
        backdropFilter="blur(20px)"
        py={{ base: 10, md: 12 }}
        position="relative"
      >
        <Container maxW="container.xl">
          <VStack spacing={6}>
            {/* Logo + name */}
            <HStack spacing={3}>
              <Box
                transition="all 0.3s"
                _hover={{
                  transform: 'scale(1.08)',
                  filter: 'drop-shadow(0 0 10px rgba(255,152,0,0.5))'
                }}
              >
                <Image
                  src="/images/logo.png"
                  alt="Namaskar India Logo"
                  w="44px"
                  h="44px"
                  objectFit="contain"
                  fallback={<Spinner size="md" color="brand.500" />}
                />
              </Box>
              <Text
                bgGradient="linear(to-r, brand.400, terracotta.400)"
                bgClip="text"
                fontSize="xl"
                fontWeight="bold"
                letterSpacing="tight"
              >
                Namaskar India
              </Text>
            </HStack>

            {/* Tagline */}
            <Text fontSize="sm" color="whiteAlpha.700" textAlign="center">
              Ancient stories. Timeless wisdom. Modern ears.
            </Text>

            {/* Social icons */}
            <HStack spacing={3}>
              <SocialLink icon={FaSpotify} href="https://open.spotify.com/show/03UrDnDHONZ92oOpWzfhxx" label="Spotify" />
              <SocialLink icon={SiApplepodcasts} href="https://podcasts.apple.com/us/podcast/namaskar-india-tales-from-indian-history-and-mythology/id1516915527" label="Apple Podcasts" />
              <SocialLink icon={FaYoutube} href="https://www.youtube.com/c/NamaskarIndia" label="YouTube" />
              <SocialLink icon={FaInstagram} href="https://www.instagram.com/namaskarindialive" label="Instagram" />
              <SocialLink icon={FaFacebook} href="https://www.facebook.com/namaskarindialive" label="Facebook" />
              <SocialLink icon={FaRss} href="https://anchor.fm/s/1012c66c/podcast/rss" label="RSS Feed" />
            </HStack>

            {/* Divider */}
            <Box w="100%" h="1px" bg="rgba(255,255,255,0.06)" />

            {/* Bottom bar */}
            <HStack
              w="100%"
              justify="space-between"
              flexDirection={{ base: 'column', sm: 'row' }}
              gap={2}
            >
              <Text color="whiteAlpha.700" fontSize={{ base: "xs", md: "sm" }}>
                © {currentYear} Namaskar India. All rights reserved.
              </Text>
              <HStack spacing={3} fontSize={{ base: "xs", md: "sm" }}>
                <Link as={RouterLink} to="/privacy" color="whiteAlpha.700" _hover={{ color: 'brand.300', textDecoration: 'none' }}>
                  Privacy Policy
                </Link>
                <Text color="whiteAlpha.400">·</Text>
                <Link as={RouterLink} to="/terms" color="whiteAlpha.700" _hover={{ color: 'brand.300', textDecoration: 'none' }}>
                  Terms of Use
                </Link>
                <Text color="whiteAlpha.400">·</Text>
                <Link as={RouterLink} to="/contact" color="whiteAlpha.700" _hover={{ color: 'brand.300', textDecoration: 'none' }}>
                  Contact
                </Link>
              </HStack>
            </HStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
